import React, { useEffect, useState } from 'react';

interface ImageProps {
	src: string;
	alt?: string;
	anchor?: 'top' | 'bottom' | 'center';
}

const Image: React.FC<ImageProps> = ({ src, alt = '', anchor = 'top' }) => {
	const [imageSize, setImageSize] = useState({ width: 1, height: 1 });
	const [viewSize, setViewSize] = useState({ width: 0, height: 0 });

	useEffect(() => {
		// Load the image and get its dimensions
		const img = document.createElement('img');
		img.src = src;
		img.onload = () => {
			setImageSize({ width: img.width, height: img.height });
		};

		// Handle resizing of the container
		const updateSize = () => {
			const container = document.getElementById(`image-container-${src}`);
			if (container) {
				setViewSize({
					width: container.clientWidth,
					height: container.clientHeight,
				});
			}
		};

		window.addEventListener('resize', updateSize);
		updateSize();

		return () => window.removeEventListener('resize', updateSize);
	}, [src]);

	// Ensure the image fills the circle while maintaining aspect ratio
	const minSize = Math.min(viewSize.width, viewSize.height);
	let imageWidth = minSize;
	let imageHeight = minSize;

	if (imageSize.width > imageSize.height) {
		// Landscape image
		imageHeight = minSize;
		imageWidth = (minSize / imageSize.height) * imageSize.width;
	} else {
		// Portrait or square image
		imageWidth = minSize;
		imageHeight = (minSize / imageSize.width) * imageSize.height;
	}

	// Anchoring logic
	const anchorStyles: React.CSSProperties = {
		position: 'absolute',
		top: anchor === 'top' ? '0px' : anchor === 'bottom' ? 'auto' : `${(viewSize.height - imageHeight) / 2}px`,
		bottom: anchor === 'bottom' ? '0px' : 'auto',
		left: '50%',
		transform: 'translateX(-50%)',
		width: `${imageWidth}px`,
		height: `${imageHeight}px`,
		objectFit: 'cover', // Ensure it fills the circular space properly
	};

	return (
		<div id={`image-container-${src}`} style={{ width: '100%', height: '100%', position: 'relative' }}>
			<img src={src} alt={alt} style={anchorStyles} />
		</div>
	);
};

export default Image;
